import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { storageSettingsSectionPath } from '../services/storageService';
import {
  cloudErrorAction,
  cloudErrorCtaPath,
  cloudErrorToastMessage,
  parseCloudErrorPayload,
  type CloudErrorAction,
  type CloudErrorPayload,
} from '../services/cloudErrorGuide';

const DEDUPE_MS = 4000;
let lastToastKey = '';
let lastToastAt = 0;

function shouldShowToast(key: string): boolean {
  const now = Date.now();
  if (key === lastToastKey && now - lastToastAt < DEDUPE_MS) {
    return false;
  }
  lastToastKey = key;
  lastToastAt = now;
  return true;
}

/**
 * Global listener for cloud-error events (from API interceptors and fetch helpers).
 * Shows a short actionable toast; reconnect flows still go through storage-auth-expired.
 */
const CloudErrorHandler: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const onCloudError = (event: Event) => {
      const detail = (event as CustomEvent<{ payload: CloudErrorPayload; action: CloudErrorAction }>).detail;
      if (!detail?.payload) return;

      const { payload, action } = detail;
      const toastKey = `${payload.code}:${payload.inactiveReason ?? ''}:${action}`;
      if (!shouldShowToast(toastKey)) return;

      const message = cloudErrorToastMessage(payload, t);
      const ctaPath = cloudErrorCtaPath(action, payload.requiredStorageProvider);

      if (action === 'upgrade_or_link') {
        toast(
          (toastInstance) => (
            <div className="text-sm">
              <p>{message}</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => {
                    toast.dismiss(toastInstance.id);
                    window.location.assign(storageSettingsSectionPath());
                  }}
                >
                  {t('cloudErrors.linkPersonalCloud', 'Link personal cloud')}
                </button>
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => {
                    toast.dismiss(toastInstance.id);
                    window.location.assign('/upgrade');
                  }}
                >
                  {t('cloudErrors.upgradePlan', 'Upgrade plan')}
                </button>
              </div>
            </div>
          ),
          { duration: 9000, id: toastKey },
        );
        return;
      }

      if (ctaPath && action !== 'inform_owner_reconnect' && action !== 'inform_collaborator_reconnect') {
        toast(
          (toastInstance) => (
            <div className="text-sm">
              <p>{message}</p>
              <button
                type="button"
                className="mt-2 font-medium underline"
                onClick={() => {
                  toast.dismiss(toastInstance.id);
                  window.location.assign(ctaPath);
                }}
              >
                {t('cloudErrors.openSettings', 'Open Settings')}
              </button>
            </div>
          ),
          { duration: 8000, id: toastKey },
        );
      } else {
        toast.error(message, { duration: 7000, id: toastKey });
      }
    };

    window.addEventListener('cloud-error', onCloudError);
    return () => window.removeEventListener('cloud-error', onCloudError);
  }, [t]);

  return null;
};

export default CloudErrorHandler;
